import { useState } from 'react';
import { useRecoilValue } from 'recoil';
import { Button } from '@/components/atoms/NewPcButton';
import SearchIcon from '@/assets/icons/Monitoring/SearchIcon.svg?react';
import { LayoutStickySection } from '../orders/_layouts/LayoutOrderContainer';
import { useToast } from '@/hooks/V3/useToast';
import { useAppSelector } from '@/redux/store';
import { useLog } from '@/hooks/V3/useLog';
import { deviceUuidState } from '@/store/atom';
import useExtensionInstalled from '@/hooks/useExtensionInstalled';
import useIsChromium from '@/hooks/useIsChromium';
import useCheckMembership from '@/hooks/useCheckMembership';
import ExtensionInstallDialog from '@/pages/Monitoring/ExtensionInstallDialog';
import UnsupportedBrowserDialog from '@/pages/Monitoring/UnsupportedBrowserDialog';
import OptimizationLimitDialog from './OptimizationLimitDialog';
import OptimizationTutorialOverlay from './OptimizationTutorialOverlay';
import { useInitOptimization } from './useInitOptimization';
import { useProductOptimization } from './ProductOptimizationContext';

const isValidNaverUrl = (input: string): boolean => {
  try {
    const trimmed = input.trim();
    const urlStr = trimmed.startsWith('http') ? trimmed : `https://${trimmed}`;
    const parsed = new URL(urlStr);
    return (
      parsed.hostname === 'brand.naver.com' ||
      parsed.hostname === 'smartstore.naver.com' ||
      parsed.hostname.endsWith('.brand.naver.com') ||
      parsed.hostname.endsWith('.smartstore.naver.com')
    );
  } catch {
    return false;
  }
};

export const SearchBar = () => {
  const [url, setUrl] = useState('');
  const [extensionDialogOpen, setExtensionDialogOpen] = useState(false);
  const [unsupportedBrowserDialogOpen, setUnsupportedBrowserDialogOpen] = useState(false);
  const [limitDialogOpen, setLimitDialogOpen] = useState(false);
  const [tutorialOpen, setTutorialOpen] = useState(false);

  const isChromium = useIsChromium();
  const { isExtensionInstalled } = useExtensionInstalled();
  const isMember = useCheckMembership();
  const { showToast } = useToast();
  const user = useAppSelector((state) => state.user);
  const deviceUuid = useRecoilValue(deviceUuidState);
  const { setUserEventHistory } = useLog(user.UserNo);
  const { usage, refetchList, refetchUsage } = useProductOptimization();
  const { mutate: initMut, isPending: isIniting } = useInitOptimization();

  const usedCount = usage?.usedCount ?? 0;
  const maxCount = usage?.maxCount ?? (isMember ? 20 : 1);
  const isLimitReached = usedCount >= maxCount;

  const gateClick = () => {
    if (!isChromium) {
      setUnsupportedBrowserDialogOpen(true);
    } else if (!isExtensionInstalled) {
      setExtensionDialogOpen(true);
    } else if (isLimitReached) {
      setLimitDialogOpen(true);
    }
  };

  const handleSearch = () => {
    if (isIniting) return;
    if (!isChromium) return setUnsupportedBrowserDialogOpen(true);
    if (!isExtensionInstalled) return setExtensionDialogOpen(true);
    if (isLimitReached) return setLimitDialogOpen(true);

    if (!url.trim()) {
      showToast('warning', 'URL을 입력해주세요.');
      return;
    }
    if (!isValidNaverUrl(url)) {
      showToast('warning', 'brand.naver.com 또는 smartstore.naver.com 상품 URL을 입력해주세요.');
      return;
    }

    const trimmed = url.trim();
    const targetUrl = trimmed.startsWith('http') ? trimmed : `https://${trimmed}`;

    setUserEventHistory({
      eventName: 'optimization_product_search_web_dashboard',
      eventValue: targetUrl,
      deviceUuid,
    });

    // 팝업 차단 방지: 사용자 클릭 컨텍스트에서 먼저 탭을 열고, 성공 시 URL 로딩
    const newTab = window.open('', '_blank');

    initMut(
      { cplatToken: user.CplatToken, goodsUrl: targetUrl },
      {
        onSuccess: () => {
          refetchList();
          refetchUsage();
          if (newTab) {
            newTab.location.href = targetUrl;
          }
          setTutorialOpen(true);
          setUrl('');
        },
        onError: (err) => {
          if (newTab) newTab.close();
          const res = (err as any)?.response?.data;
          const code = res?.code as string | undefined;
          if (code === '680') {
            setLimitDialogOpen(true);
          } else if (code === '681') {
            showToast('warning', '이미 분석 중인 상품이에요.');
          } else if (code === '560') {
            showToast('warning', 'URL 형식이 올바르지 않아요.');
          } else {
            showToast('warning', '진단 시작 중 오류가 발생했어요.');
          }
        },
      },
    );
  };

  const isGated = !isChromium || !isExtensionInstalled || isLimitReached;

  return (
    <>
      <UnsupportedBrowserDialog
        open={unsupportedBrowserDialogOpen}
        onOpenChange={setUnsupportedBrowserDialogOpen}
      />
      <ExtensionInstallDialog
        open={extensionDialogOpen}
        onOpenChange={setExtensionDialogOpen}
      />
      <OptimizationLimitDialog
        open={limitDialogOpen}
        onOpenChange={setLimitDialogOpen}
        isMember={isMember}
        maxCount={maxCount}
      />
      <OptimizationTutorialOverlay open={tutorialOpen} onClose={() => setTutorialOpen(false)} />

      <LayoutStickySection className="max-w-full border-none bg-gr-50/90 px-0 py-[10px]">
        <div
          className={`flex h-[54px] max-w-full rounded-[16px] border border-gr-300 bg-white px-[12px] py-[10px] focus-within:border-point-400 ${
            isGated ? 'cursor-pointer' : ''
          }`}
          onClick={isGated ? gateClick : undefined}
        >
          <input
            type="text"
            placeholder="진단할 스마트스토어 상품의 Url을 입력해주세요"
            className="h-full flex-1 pr-[10px] text-gr-800 Body5M14 focus:outline-none"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            readOnly={isGated}
            tabIndex={isGated ? -1 : undefined}
          />
          <Button
            className="h-[34px] min-w-[67px] gap-[6px] px-[10px] py-[6px] Body5M14"
            onClick={handleSearch}
            disabled={isIniting}
          >
            <SearchIcon />
            검색
          </Button>
        </div>
      </LayoutStickySection>
    </>
  );
};

export default SearchBar;
